import React, { useEffect, useMemo, useState } from 'react';
import { BayenahLogo, LOGO_SIZES } from '@/components/brand/BayenahLogo';
import { cn } from '@/lib/utils';

const STORAGE_KEY = 'bayenah-legal-language';

export const PrivacyPage = () => {
  const [language, setLanguage] = useState('en');

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'ar' || saved === 'en') {
      setLanguage(saved);
      return;
    }
    const browserLang = navigator.language || navigator.userLanguage || 'en';
    setLanguage(browserLang.toLowerCase().startsWith('ar') ? 'ar' : 'en');
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language);
  }, [language]);

  const isArabic = language === 'ar';

  const content = useMemo(() => ({
    en: {
      metaTitle: 'Bayenah Privacy Policy',
      metaDescription:
        'Learn how Bayenah collects, uses, and protects your information when you use our website and services.',
      title: 'Bayenah Privacy Policy',
      lastUpdated: 'Last updated: 24 Feb 2026',
      sections: [
        {
          title: '1) Overview',
          paragraphs: [
            'Bayenah (“we”, “us”, “our”) respects your privacy. This Privacy Policy explains what information we collect, how we use it, and the choices you have when you use bayenah.com (the “Service”).',
          ],
        },
        {
          title: '2) Information We Collect',
          subpoints: [
            {
              label: 'a) Account Information:',
              text: 'When you sign in (including via Google), we may collect your name, email address, and profile photo (if provided by the identity provider).',
            },
            {
              label: 'b) Usage Data:',
              text: 'We may collect basic analytics and technical data such as device type, browser type, IP address, pages visited, and approximate location derived from IP.',
            },
            {
              label: 'c) Communications:',
              text: 'If you contact us, we may keep a record of the message and your contact details.',
            },
          ],
        },
        {
          title: '3) How We Use Information',
          paragraphs: ['We use information to:'],
          list: [
            'Provide and operate the Service (authentication, access, and core features)',
            'Improve performance, reliability, and user experience',
            'Prevent fraud, abuse, and security incidents',
            'Communicate important updates about the Service',
          ],
        },
        {
          title: '4) Cookies and Similar Technologies',
          paragraphs: [
            'We may use cookies or similar technologies for authentication, security, and basic analytics. You can control cookies through your browser settings, but some features may not function properly if cookies are disabled.',
          ],
        },
        {
          title: '5) Sharing of Information',
          paragraphs: ['We do not sell your personal information.', 'We may share limited information only when necessary:'],
          list: [
            'With service providers who help us operate the Service (e.g., hosting, analytics, authentication) under confidentiality obligations',
            'To comply with legal requirements or protect rights, safety, and security',
          ],
        },
        {
          title: '6) Data Retention',
          paragraphs: [
            'We retain personal information only as long as needed to provide the Service, comply with legal obligations, resolve disputes, and enforce agreements.',
          ],
        },
        {
          title: '7) Security',
          paragraphs: [
            'We implement reasonable technical and organizational measures to protect your information. However, no online service is 100% secure.',
          ],
        },
        {
          title: '8) Your Choices and Rights',
          paragraphs: ['You can:'],
          list: [
            'Request access, correction, or deletion of your personal information (subject to applicable laws)',
            'Withdraw consent where applicable',
          ],
          footer: 'To make a request, contact us at:',
        },
        {
          title: '9) Children’s Privacy',
          paragraphs: ['The Service is not intended for children under 13. We do not knowingly collect personal information from children under 13.'],
        },
        {
          title: '10) Changes to this Policy',
          paragraphs: ['We may update this Privacy Policy from time to time. We will update the “Last updated” date and, if changes are significant, provide additional notice.'],
        },
        {
          title: '11) Contact',
          paragraphs: ['For privacy questions, contact:', 'Email:'],
        },
      ],
      email: 'hello@bayenah.com',
    },
    ar: {
      metaTitle: 'سياسة الخصوصية - بَيّنَة',
      metaDescription:
        'تعرّف على كيفية جمع واستخدام وحماية بياناتك عند استخدام موقع وخدمات بَيّنَة.',
      title: 'سياسة الخصوصية – بَيّنَة',
      lastUpdated: 'آخر تحديث: 24 فبراير 2026',
      sections: [
        {
          title: '1) نظرة عامة',
          paragraphs: [
            'نحن في “بَيّنَة” (“نحن/لنا”) نحترم خصوصيتك. تشرح هذه السياسة المعلومات التي نجمعها وكيف نستخدمها والخيارات المتاحة لك عند استخدام bayenah.com (“الخدمة”).',
          ],
        },
        {
          title: '2) المعلومات التي نجمعها',
          subpoints: [
            {
              label: 'أ) معلومات الحساب:',
              text: 'عند تسجيل الدخول (بما في ذلك عبر Google) قد نجمع الاسم والبريد الإلكتروني وصورة الملف الشخصي (إذا وفرها مزود تسجيل الدخول).',
            },
            {
              label: 'ب) بيانات الاستخدام:',
              text: 'قد نجمع بيانات تقنية وتحليلية أساسية مثل نوع الجهاز والمتصفح وعنوان IP والصفحات التي تمت زيارتها وموقع تقريبي مشتق من عنوان IP.',
            },
            {
              label: 'ج) المراسلات:',
              text: 'إذا تواصلت معنا، قد نحتفظ بسجل للرسالة وبيانات التواصل.',
            },
          ],
        },
        {
          title: '3) كيف نستخدم المعلومات',
          paragraphs: ['نستخدم المعلومات من أجل:'],
          list: [
            'تقديم وتشغيل الخدمة (المصادقة، الوصول، والوظائف الأساسية)',
            'تحسين الأداء والاعتمادية وتجربة المستخدم',
            'منع الاحتيال وسوء الاستخدام والحوادث الأمنية',
            'إرسال تحديثات مهمة تتعلق بالخدمة',
          ],
        },
        {
          title: '4) ملفات تعريف الارتباط وتقنيات مشابهة',
          paragraphs: [
            'قد نستخدم ملفات تعريف الارتباط أو تقنيات مشابهة للمصادقة والأمان والتحليلات الأساسية. يمكنك التحكم بها من إعدادات المتصفح، لكن قد لا تعمل بعض الميزات بشكل صحيح إذا تم تعطيلها.',
          ],
        },
        {
          title: '5) مشاركة المعلومات',
          paragraphs: ['نحن لا نبيع معلوماتك الشخصية.', 'قد نشارك معلومات محدودة فقط عند الحاجة:'],
          list: [
            'مع مزودي خدمات يساعدوننا في تشغيل الخدمة (مثل الاستضافة والتحليلات والمصادقة) ضمن التزامات سرية',
            'للامتثال للمتطلبات القانونية أو لحماية الحقوق والسلامة والأمن',
          ],
        },
        {
          title: '6) الاحتفاظ بالبيانات',
          paragraphs: ['نحتفظ بالمعلومات الشخصية فقط للمدة اللازمة لتقديم الخدمة والامتثال للالتزامات القانونية وحل النزاعات وإنفاذ الاتفاقيات.'],
        },
        {
          title: '7) الأمان',
          paragraphs: ['نطبق إجراءات تقنية وتنظيمية معقولة لحماية معلوماتك، لكن لا توجد خدمة عبر الإنترنت آمنة بنسبة 100%.'],
        },
        {
          title: '8) حقوقك وخياراتك',
          paragraphs: ['يمكنك:'],
          list: [
            'طلب الوصول إلى معلوماتك الشخصية أو تصحيحها أو حذفها (وفق الأنظمة المعمول بها)',
            'سحب الموافقة عندما ينطبق ذلك',
          ],
          footer: 'لتقديم طلب، تواصل معنا على:',
        },
        {
          title: '9) خصوصية الأطفال',
          paragraphs: ['الخدمة غير موجهة للأطفال دون 13 عامًا. لا نجمع عن قصد بيانات شخصية من الأطفال دون 13 عامًا.'],
        },
        {
          title: '10) التغييرات على هذه السياسة',
          paragraphs: ['قد نقوم بتحديث هذه السياسة من وقت لآخر. سنقوم بتحديث تاريخ “آخر تحديث”، وإذا كانت التغييرات جوهرية قد نقدم إشعارًا إضافيًا.'],
        },
        {
          title: '11) التواصل',
          paragraphs: ['للاستفسارات المتعلقة بالخصوصية:', 'البريد الإلكتروني:'],
        },
      ],
      email: 'hello@bayenah.com',
    },
  }), []);

  const t = content[language] || content.en;

  useEffect(() => {
    document.title = t.metaTitle;
    const description = document.querySelector('meta[name="description"]');
    if (description) {
      description.setAttribute('content', t.metaDescription);
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      meta.setAttribute('content', t.metaDescription);
      document.head.appendChild(meta);
    }
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://bayenah.com/privacy');
  }, [t]);

  const handleLanguageChange = (value) => {
    setLanguage(value);
    localStorage.setItem(STORAGE_KEY, value);
  };

  return (
    <div
      className="min-h-screen geometric-bg text-[var(--text-primary)]"
      dir={isArabic ? 'rtl' : 'ltr'}
      data-testid="privacy-page"
    >
      <div className="absolute inset-0 geometric-pattern opacity-40 pointer-events-none" />
      <div className="relative z-10">
        <header className="border-b border-white/10 bg-black/30 backdrop-blur-md" data-testid="privacy-header">
          <div className="max-w-4xl mx-auto flex items-center justify-between px-4 py-4">
            <a href="/" className="hover:opacity-80 transition-opacity" data-testid="privacy-home-link">
              <BayenahLogo height={LOGO_SIZES.headerMobile} />
            </a>
            <div className="flex items-center gap-2 text-xs font-semibold">
              <button
                type="button"
                onClick={() => handleLanguageChange('en')}
                className={cn(
                  'px-2 py-1 rounded-md border border-white/10',
                  language === 'en' ? 'bg-white/10 text-white' : 'text-[var(--text-secondary)]'
                )}
                data-testid="privacy-toggle-en"
              >
                EN
              </button>
              <span className="text-[var(--text-secondary)]">|</span>
              <button
                type="button"
                onClick={() => handleLanguageChange('ar')}
                className={cn(
                  'px-2 py-1 rounded-md border border-white/10',
                  language === 'ar' ? 'bg-white/10 text-white' : 'text-[var(--text-secondary)]',
                  'font-arabic'
                )}
                data-testid="privacy-toggle-ar"
              >
                العربية
              </button>
            </div>
          </div>
        </header>

        <main className="px-4 py-8 md:py-12">
          <div className="max-w-4xl mx-auto">
            <div className="glass-panel-strong rounded-2xl p-6 md:p-10" data-testid="privacy-content">
              <h1
                className={cn(
                  'text-2xl md:text-3xl font-bold text-white mb-2',
                  isArabic && 'font-arabic'
                )}
                data-testid="privacy-title"
              >
                {t.title}
              </h1>
              <p className={cn('text-sm text-[var(--text-secondary)] mb-8', isArabic && 'font-arabic')} data-testid="privacy-last-updated">
                {t.lastUpdated}
              </p>

              <div className="space-y-8">
                {t.sections.map((section, index) => (
                  <section key={section.title} data-testid={`privacy-section-${index + 1}`}>
                    <h2
                      className={cn('text-lg font-semibold text-white mb-3', isArabic && 'font-arabic')}
                      data-testid={`privacy-section-${index + 1}-title`}
                    >
                      {section.title}
                    </h2>
                    {section.paragraphs?.map((paragraph, pIndex) => (
                      <p
                        key={pIndex}
                        className={cn('text-[var(--text-secondary)] leading-relaxed mb-3', isArabic && 'font-arabic')}
                        data-testid={`privacy-section-${index + 1}-paragraph-${pIndex + 1}`}
                      >
                        {paragraph}
                      </p>
                    ))}
                    {section.subpoints?.map((point, pIndex) => (
                      <p
                        key={pIndex}
                        className={cn('text-[var(--text-secondary)] leading-relaxed mb-3', isArabic && 'font-arabic')}
                        data-testid={`privacy-section-${index + 1}-subpoint-${pIndex + 1}`}
                      >
                        <span className="font-semibold text-white">{point.label} </span>
                        {point.text}
                      </p>
                    ))}
                    {section.list && (
                      <ul
                        className={cn(
                          'space-y-2 text-[var(--text-secondary)] leading-relaxed',
                          isArabic ? 'pr-5 list-disc' : 'pl-5 list-disc',
                          isArabic && 'font-arabic'
                        )}
                        data-testid={`privacy-section-${index + 1}-list`}
                      >
                        {section.list.map((item, listIndex) => (
                          <li key={listIndex} data-testid={`privacy-section-${index + 1}-list-item-${listIndex + 1}`}>
                            {item}
                          </li>
                        ))}
                      </ul>
                    )}
                    {section.footer && (
                      <p
                        className={cn('text-[var(--text-secondary)] leading-relaxed mt-3', isArabic && 'font-arabic')}
                        data-testid={`privacy-section-${index + 1}-footer`}
                      >
                        {section.footer}{' '}
                        <a
                          href={`mailto:${t.email}`}
                          className="text-[var(--accent)] hover:text-white transition-colors"
                          data-testid={`privacy-section-${index + 1}-email`}
                        >
                          {t.email}
                        </a>
                      </p>
                    )}
                    {section.title === '11) Contact' && (
                      <p className={cn('text-[var(--text-secondary)] leading-relaxed mt-2', isArabic && 'font-arabic')}>
                        <a
                          href={`mailto:${t.email}`}
                          className="text-[var(--accent)] hover:text-white transition-colors"
                          data-testid="privacy-contact-email"
                        >
                          {t.email}
                        </a>
                      </p>
                    )}
                    {section.title === '11) التواصل' && (
                      <p className={cn('text-[var(--text-secondary)] leading-relaxed mt-2', isArabic && 'font-arabic')}>
                        <a
                          href={`mailto:${t.email}`}
                          className="text-[var(--accent)] hover:text-white transition-colors"
                          data-testid="privacy-contact-email"
                        >
                          {t.email}
                        </a>
                      </p>
                    )}
                  </section>
                ))}
              </div>
            </div>
          </div>
        </main>

        <footer className="border-t border-white/10 bg-black/30 px-4 py-6" data-testid="privacy-footer">
          <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-center gap-3 text-xs text-[var(--text-secondary)]">
            <span data-testid="privacy-footer-copy">© 2026 {isArabic ? 'بَيّنَة' : 'Bayenah'}</span>
            <span>•</span>
            <a
              href="/privacy"
              className="hover:text-white transition-colors"
              data-testid="privacy-footer-privacy-link"
            >
              {isArabic ? 'الخصوصية' : 'Privacy'}
            </a>
            <span>•</span>
            <a
              href="/terms"
              className="hover:text-white transition-colors"
              data-testid="privacy-footer-terms-link"
            >
              {isArabic ? 'الشروط' : 'Terms'}
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
};
